import { execAdminForm } from '../utils';

interface ValidateCatalogUploadVariables {
  partnerCode?: string;
  source?: string;
  validationId?: string;
  first?: number;
  after?: string | null;
}

export interface ValidationError {
  field: string;
  value: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface PreviewRecord {
  rowNumber: number;
  action: 'create' | 'update';
  sku: string;
  values: Record<string, unknown>;
  existingValues: Record<string, unknown> | null;
  validationErrors: ValidationError[];
}

export interface PreviewRecordEdge {
  cursor: string;
  node: PreviewRecord;
}

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

export interface PreviewRecordsConnection {
  totalCount: number;
  edges: PreviewRecordEdge[];
  pageInfo: PageInfo;
}

export interface CatalogUploadValidation {
  validationId: string;
  valid: boolean;
  rowCount: number;
  columnCount: number;
  columns: string[];
  expectedCreates: number;
  expectedUpdates: number;
  errors: string[];
  hasValidationErrors?: boolean;
  totalValidationErrors?: number;
  expiresAt?: string;
  previewRecords?: PreviewRecordsConnection;
}

interface ValidateCatalogUploadResponse {
  validateCatalogUpload: CatalogUploadValidation;
}

const VALIDATE_CATALOG_UPLOAD_MUTATION = `
  mutation validateCatalogUpload(
    $partnerCode: String
    $source: String
    $validationId: String
    $first: Int
    $after: String
  ) {
    validateCatalogUpload(
      partnerCode: $partnerCode
      source: $source
      validationId: $validationId
      first: $first
      after: $after
    ) {
      validationId
      valid
      rowCount
      columnCount
      columns
      expectedCreates
      expectedUpdates
      errors
      hasValidationErrors
      totalValidationErrors
      expiresAt
      previewRecords {
        totalCount
        edges {
          cursor
          node {
            rowNumber
            action
            sku
            values
            existingValues
            validationErrors {
              field
              value
              message
              severity
            }
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

export const validateCatalogUpload = async (
  variables: ValidateCatalogUploadVariables,
  file?: File,
): Promise<CatalogUploadValidation> => {
  const data = file ?
    await execAdminForm<ValidateCatalogUploadVariables, ValidateCatalogUploadResponse>(
      VALIDATE_CATALOG_UPLOAD_MUTATION,
      variables,
      { file },
    ) :
    await execAdminForm<ValidateCatalogUploadVariables, ValidateCatalogUploadResponse>(
      VALIDATE_CATALOG_UPLOAD_MUTATION,
      variables,
      {},
    );

  // Backend now consistently parses JSONB fields via parseJsonValue()
  // so we can return the result directly without additional type coercion
  return data.validateCatalogUpload;
};
